"use client"
import React, { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import  { Label }  from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import {
  Zap, Bot, Users, Code, Play, Square, Settings, AlertTriangle, Copy, Download, Activity, Target,
  Smartphone, Monitor, Globe, Shield, Crosshair, Eye, Timer, Network, Cpu, BarChart3, Lock, 
  Fingerprint, MousePointer, Server
} from "lucide-react";

interface InjectionStats {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  avgResponseTime: number;
  detectedAsBot: number;
  detectedAsHuman: number;
  bypassedSecurity: number;
  triggeredAlerts: number;
}

interface AttackPattern {
  id: string;
  name: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: string;
}

type InjectionStatus = "stopped" | "running" | "paused";
type InjectionMode = "stealth" | "aggressive" | "mixed" | "custom";

const ATTACK_PATTERNS: AttackPattern[] = [
  { id: 'human_mimic', name: 'Human Mimicry', description: 'Advanced human behavior simulation', severity: 'low', category: 'Evasion' },
  { id: 'bot_flood', name: 'Bot Flood', description: 'High-volume automated requests', severity: 'high', category: 'Volume' },
  { id: 'slow_burn', name: 'Slow Burn', description: 'Gradual traffic increase to avoid detection', severity: 'medium', category: 'Stealth' },
  { id: 'fingerprint_rotation', name: 'Fingerprint Rotation', description: 'Dynamic browser fingerprint changes', severity: 'high', category: 'Evasion' },
  { id: 'distributed_swarm', name: 'Distributed Swarm', description: 'Coordinated multi-source attack', severity: 'critical', category: 'Advanced' },
  { id: 'captcha_solver', name: 'CAPTCHA Solver', description: 'Automated CAPTCHA bypass attempts', severity: 'critical', category: 'Bypass' },
  { id: 'session_hijack', name: 'Session Mimicry', description: 'Legitimate session pattern replication', severity: 'high', category: 'Advanced' },
  { id: 'headless_detection', name: 'Headless Evasion', description: 'Anti-headless browser detection bypass', severity: 'high', category: 'Evasion' }
];

const USER_AGENTS = [
  'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
  'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
  'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
  'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:121.0) Gecko/20100101 Firefox/121.0',
  'Mozilla/5.0 (Macintosh; Intel Mac OS X 10.15; rv:121.0) Gecko/20100101 Firefox/121.0',
  'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.2 Safari/605.1.15'
];

// Enhanced utility functions for advanced bot detection bypass
const AdvancedEvasionTechniques = {
  // Browser fingerprint randomization
  randomizeFingerprint: () => {
    const fingerprints = [
      {
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
        screen: { width: 1920, height: 1080, colorDepth: 24 },
        timezone: -480,
        language: 'en-US',
        platform: 'Win32'
      },
      {
        userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
        screen: { width: 2560, height: 1440, colorDepth: 24 },
        timezone: -420,
        language: 'en-US',
        platform: 'MacIntel'
      },
      {
        userAgent: 'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
        screen: { width: 1366, height: 768, colorDepth: 24 },
        timezone: 0,
        language: 'en-GB',
        platform: 'Linux x86_64'
      }
    ];
    return fingerprints[Math.floor(Math.random() * fingerprints.length)];
  },

  // Human-like timing patterns
  generateHumanTiming: (baseDelay = 1000) => {
    const variance = baseDelay * 0.3;
    const human_delay = baseDelay + (Math.random() - 0.5) * variance;
    return Math.max(100, human_delay);
  },

  // Mouse movement simulation with natural curves
  generateMousePath: (startX: number, startY: number, endX: number, endY: number, steps = 10) => {
    const path = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      
      // Add bezier curve for more natural movement
      const controlX = (startX + endX) / 2 + (Math.random() - 0.5) * 50;
      const controlY = (startY + endY) / 2 + (Math.random() - 0.5) * 50;
      
      // Quadratic bezier curve calculation
      const x = Math.pow(1 - t, 2) * startX + 2 * (1 - t) * t * controlX + Math.pow(t, 2) * endX;
      const y = Math.pow(1 - t, 2) * startY + 2 * (1 - t) * t * controlY + Math.pow(t, 2) * endY;
      
      // Add small random jitter for human-like imperfection
      const jitterX = (Math.random() - 0.5) * 3;
      const jitterY = (Math.random() - 0.5) * 3;
      
      path.push({ 
        x: Math.round(x + jitterX), 
        y: Math.round(y + jitterY),
        timestamp: Date.now() + i * AdvancedEvasionTechniques.generateHumanTiming(50)
      });
    }
    return path;
  },

  // Keyboard event simulation
  simulateKeyboardEvents: async (element: HTMLInputElement, text: string) => {
    for (let i = 0; i < text.length; i++) {
      const char = text.charAt(i);
      element.dispatchEvent(new KeyboardEvent('keydown', { key: char, bubbles: true }));
      element.dispatchEvent(new KeyboardEvent('keypress', { key: char, bubbles: true }));
      element.value += char;
      element.dispatchEvent(new Event('input', { bubbles: true }));
      element.dispatchEvent(new KeyboardEvent('keyup', { key: char, bubbles: true }));
      await new Promise(resolve => setTimeout(resolve, AdvancedEvasionTechniques.generateHumanTiming(100)));
    }
  }
};

export default function AdvancedTrafficInjection() {
  const [injectionStatus, setInjectionStatus] = useState<InjectionStatus>("stopped");
  const [injectionMode, setInjectionMode] = useState<InjectionMode>("stealth");
  const [targetUrl, setTargetUrl] = useState("https://dashboard-dn6.pages.dev");
  const [requestsPerSecond, setRequestsPerSecond] = useState([10]);
  const [duration, setDuration] = useState(300);
  const [selectedPatterns, setSelectedPatterns] = useState<string[]>(['human_mimic']);
  const [proxyList, setProxyList] = useState("");
  const [customHeaders, setCustomHeaders] = useState<{[key: string]: string}>({});
  const [useRotation, setUseRotation] = useState(true);
  const [bypassMode, setBypassMode] = useState(false);
  const [stealthLevel, setStealthLevel] = useState([70]);
  
  const [currentStats, setCurrentStats] = useState<InjectionStats>({
    totalRequests: 0,
    successfulRequests: 0,
    failedRequests: 0,
    avgResponseTime: 0,
    detectedAsBot: 0,
    detectedAsHuman: 0,
    bypassedSecurity: 0,
    triggeredAlerts: 0
  });

  const [realTimeMetrics, setRealTimeMetrics] = useState({
    currentRPS: 0,
    successRate: 0,
    detectionRate: 0,
    bypassRate: 0
  });

  const [injectionProgress, setInjectionProgress] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const metricsRef = useRef<NodeJS.Timeout | null>(null);

  // Advanced script generators
  const generateStealthScript = useCallback(() => {
    return `
// Advanced Stealth Traffic Injection
class StealthInjector {
  constructor(config) {
    this.config = config;
    this.sessionData = {};
    this.fingerprints = this.generateFingerprints();
    this.behaviorPatterns = this.loadBehaviorPatterns();
  }

  generateFingerprints() {
    const screens = [
      { width: 1920, height: 1080, colorDepth: 24 },
      { width: 1366, height: 768, colorDepth: 24 },
      { width: 1440, height: 900, colorDepth: 24 },
      { width: 1536, height: 864, colorDepth: 24 }
    ];
    
    const timezones = [-480, -420, -360, -300, -240, -180, -120, 0, 60, 120, 180, 240, 300, 360, 420, 480, 540];
    const languages = ['en-US', 'en-GB', 'de-DE', 'fr-FR', 'es-ES', 'it-IT', 'pt-BR', 'ru-RU', 'ja-JP', 'ko-KR'];
    
    return {
      screen: screens[Math.floor(Math.random() * screens.length)],
      timezone: timezones[Math.floor(Math.random() * timezones.length)],
      language: languages[Math.floor(Math.random() * languages.length)],
      platform: ['Win32', 'MacIntel', 'Linux x86_64'][Math.floor(Math.random() * 3)],
      hardwareConcurrency: [2, 4, 8, 12, 16][Math.floor(Math.random() * 5)]
    };
  }

  async simulateHumanBehavior() {
    // Mouse movement patterns
    const mousePatterns = [
      () => this.simulateMouseMovement(),
      () => this.simulateScrollBehavior(),
      () => this.simulateClickPatterns(),
      () => this.simulateKeyboardEvents()
    ];

    // Random execution order
    const shuffled = mousePatterns.sort(() => Math.random() - 0.5);
    
    for (const pattern of shuffled) {
      await pattern();
      await this.humanDelay();
    }
  }

  simulateMouseMovement() {
    const events = ['mousemove', 'mouseenter', 'mouseleave', 'mouseover'];
    const path = this.generateNaturalMousePath();
    
    path.forEach((point, index) => {
      setTimeout(() => {
        try {
          document.dispatchEvent(new MouseEvent('mousemove', {
            clientX: point.x,
            clientY: point.y,
            bubbles: true
          }));
        } catch (e) {}
      }, index * 50);
    });
  }

  generateNaturalMousePath() {
    const path = [];
    let startX = Math.random() * window.innerWidth;
    let startY = Math.random() * window.innerHeight;
    
    for (let i = 0; i < 20; i++) {
      startX += (Math.random() - 0.5) * 100;
      startY += (Math.random() - 0.5) * 100;
      
      startX = Math.max(0, Math.min(window.innerWidth, startX));
      startY = Math.max(0, Math.min(window.innerHeight, startY));
      
      path.push({ x: startX, y: startY });
    }
    
    return path;
  }

  async simulateScrollBehavior() {
    const scrollPatterns = [
      () => this.smoothScroll(),
      () => this.jumpScroll(),
      () => this.readingScroll()
    ];
    
    const pattern = scrollPatterns[Math.floor(Math.random() * scrollPatterns.length)];
    await pattern();
  }

  async smoothScroll() {
    const totalHeight = document.body.scrollHeight;
    const viewportHeight = window.innerHeight;
    const scrollSteps = Math.floor(totalHeight / viewportHeight / 3);
    
    for (let i = 0; i < scrollSteps; i++) {
      window.scrollBy({
        top: viewportHeight / 3,
        behavior: 'smooth'
      });
      await this.humanDelay(800, 2000);
    }
  }

  async advancedRequest(url, options = {}) {
    const fingerprint = this.fingerprints;
    
    const headers = {
      'User-Agent': this.getRandomUserAgent(),
      'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
      'Accept-Language': fingerprint.language + ',en;q=0.5',
      'Accept-Encoding': 'gzip, deflate, br',
      'DNT': '1',
      'Connection': 'keep-alive',
      'Upgrade-Insecure-Requests': '1',
      'Sec-Fetch-Dest': 'document',
      'Sec-Fetch-Mode': 'navigate',
      'Sec-Fetch-Site': 'none',
      'Cache-Control': 'max-age=0',
      ...options.headers
    };

    try {
      const response = await fetch(url, {
        method: options.method || 'GET',
        headers,
        credentials: 'include',
        ...options
      });
      
      await this.processResponse(response);
      return response;
    } catch (error) {
      console.log('Request failed:', error.message);
      throw error;
    }
  }

  getRandomUserAgent() {
    const agents = ${JSON.stringify(USER_AGENTS)};
    return agents[Math.floor(Math.random() * agents.length)];
  }

  async processResponse(response) {
    // Analyze response for bot detection patterns
    const text = await response.text();
    
    const detectionPatterns = [
      /captcha/i,
      /bot.?detect/i,
      /access.?denied/i,
      /rate.?limit/i,
      /suspicious.?activity/i
    ];
    
    const detected = detectionPatterns.some(pattern => pattern.test(text));
    
    if (detected) {
      console.log('Potential bot detection triggered');
      await this.adaptBehavior();
    }
  }

  async adaptBehavior() {
    // Adaptive behavior when detection is suspected
    const adaptations = [
      () => this.increaseHumanLikeness(),
      () => this.changeFingerprint(),
      () => this.adjustTiming(),
      () => this.switchUserAgent()
    ];
    
    const adaptation = adaptations[Math.floor(Math.random() * adaptations.length)];
    await adaptation();
  }

  async increaseHumanLikeness() {
    // Increase delays and add more human-like interactions
    await this.simulateTyping();
    await this.simulateReading();
    await this.simulateNavigation();
  }

  async simulateTyping() {
    const inputs = document.querySelectorAll('input[type="text"], input[type="email"], textarea');
    if (inputs.length > 0) {
      const input = inputs[Math.floor(Math.random() * inputs.length)];
      const text = this.generateRandomText();
      
      for (let char of text) {
        input.value += char;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        await this.humanDelay(100, 300);
      }
    }
  }

  generateRandomText() {
    const words = ['hello', 'world', 'test', 'user', 'example', 'demo', 'sample'];
    const length = Math.floor(Math.random() * 5) + 3;
    return Array.from({length}, () => words[Math.floor(Math.random() * words.length)]).join(' ');
  }

  async humanDelay(min = 1000, max = 3000) {
    const delay = Math.random() * (max - min) + min;
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  async execute() {
    console.log('Starting stealth injection...');
    
    try {
      await this.simulateHumanBehavior();
      const response = await this.advancedRequest('${targetUrl}');
      
      if (response.ok) {
        console.log('Request successful, response analyzed');
      }
    } catch (error) {
      console.log('Execution error:', error.message);
    }
  }
}

// Initialize and execute
const injector = new StealthInjector({
  stealthLevel: ${stealthLevel[0]},
  adaptiveBehavior: true,
  fingerprintRotation: ${useRotation}
});

injector.execute();
`;
  }, [stealthLevel, useRotation, targetUrl]);

  const generateAggressiveScript = useCallback(() => {
    return `
// Aggressive Traffic Injection
class AggressiveInjector {
  constructor(config) {
    this.config = config;
    this.requestQueue = [];
    this.concurrentLimit = config.concurrent || 20;
    this.running = false;
  }

  async rapidFire() {
    console.log('Starting aggressive injection...');
    this.running = true;
    
    const requests = [];
    const rps = ${requestsPerSecond[0]};
    
    for (let i = 0; i < rps && this.running; i++) {
      requests.push(this.executeRequest(i));
      
      if (requests.length >= this.concurrentLimit) {
        await Promise.allSettled(requests);
        requests.length = 0;
      }
    }
    
    if (requests.length > 0) {
      await Promise.allSettled(requests);
    }
  }

  async executeRequest(index) {
    const headers = {
      'User-Agent': this.getAggressiveUserAgent(),
      'Accept': '*/*',
      'Connection': 'keep-alive',
      ...this.getCustomHeaders()
    };

    try {
      const response = await fetch('${targetUrl}', {
        method: 'GET',
        headers,
        cache: 'no-cache'
      });
      
      console.log(\`Request \${index}: \${response.status}\`);
      return response;
    } catch (error) {
      console.log(\`Request \${index} failed: \${error.message}\`);
      throw error;
    }
  }

  getAggressiveUserAgent() {
    return 'Mozilla/5.0 (compatible; TestBot/1.0; +http://example.com/bot)';
  }

  getCustomHeaders() {
    return {
      'X-Forwarded-For': this.generateRandomIP(),
      'X-Real-IP': this.generateRandomIP(),
      'X-Originating-IP': this.generateRandomIP()
    };
  }

  generateRandomIP() {
    return Array.from({length: 4}, () => Math.floor(Math.random() * 256)).join('.');
  }

  stop() {
    this.running = false;
    console.log('Aggressive injection stopped');
  }
}

const injector = new AggressiveInjector({
  concurrent: 20,
  rps: ${requestsPerSecond[0]}
});

injector.rapidFire();
`;
  }, [requestsPerSecond, targetUrl]);

  const generateBypassScript = useCallback(() => {
    return `
// Advanced Security Bypass Injection
class BypassInjector {
  constructor() {
    this.techniques = [
      'userAgentRotation',
      'headerSpoofing',
      'timingRandomization',
      'sessionPersistence',
      'cookieManipulation',
      'referrerSpoofing'
    ];
  }

  async executeBypass() {
    console.log('Initializing bypass techniques...');
    
    // Technique 1: Advanced User Agent Rotation
    await this.rotateUserAgent();
    
    // Technique 2: Browser Fingerprint Spoofing
    await this.spoofFingerprint();
    
    // Technique 3: Session Management
    await this.manageSession();
    
    // Technique 4: Anti-Detection Measures
    await this.antiDetection();
    
    // Execute the actual request
    await this.executeStealthRequest();
  }

  async rotateUserAgent() {
    const agents = ${JSON.stringify(USER_AGENTS)};
    const selected = agents[Math.floor(Math.random() * agents.length)];
    
    Object.defineProperty(navigator, 'userAgent', {
      get: function() { return selected; },
      configurable: true
    });
  }

  async spoofFingerprint() {
    // Spoof screen resolution
    Object.defineProperty(screen, 'width', {
      get: function() { return 1920 + Math.floor(Math.random() * 100); }
    });
    
    Object.defineProperty(screen, 'height', {
      get: function() { return 1080 + Math.floor(Math.random() * 100); }
    });
    
    // Spoof timezone
    Object.defineProperty(Date.prototype, 'getTimezoneOffset', {
      value: function() { return -480 + Math.floor(Math.random() * 960); }
    });
    
    // Spoof language
    Object.defineProperty(navigator, 'language', {
      get: function() { 
        const langs = ['en-US', 'en-GB', 'de-DE', 'fr-FR'];
        return langs[Math.floor(Math.random() * langs.length)];
      }
    });
  }

  async manageSession() {
    // Create persistent session cookies
    document.cookie = \`session_id=\${this.generateSessionId()}; path=/; max-age=86400\`;
    document.cookie = \`csrf_token=\${this.generateCSRFToken()}; path=/; max-age=86400\`;
    
    // Store session data
    try {
      const sessionData = {
        timestamp: Date.now(),
        fingerprint: this.generateFingerprint(),
        visitCount: (parseInt(localStorage.getItem('visit_count')) || 0) + 1
      };
      
      localStorage.setItem('session_data', JSON.stringify(sessionData));
      localStorage.setItem('visit_count', sessionData.visitCount.toString());
    } catch (e) {
      console.log('Session storage not available, using memory');
    }
  }

  generateSessionId() {
    return Array.from({length: 32}, () => 
      Math.floor(Math.random() * 16).toString(16)
    ).join('');
  }

  generateCSRFToken() {
    return btoa(Math.random().toString()).substring(0, 32);
  }

  generateFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Browser fingerprint', 2, 2);
    return canvas.toDataURL();
  }

  async antiDetection() {
    // Override detection methods
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    EventTarget.prototype.addEventListener = function(type, listener, options) {
      if (type === 'beforeunload' || type === 'unload') {
        return; // Block unload detection
      }
      return originalAddEventListener.call(this, type, listener, options);
    };
    
    // Override fetch to add realistic delays
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      await new Promise(resolve => setTimeout(resolve, Math.random() * 100 + 50));
      return originalFetch.apply(this, args);
    };
  }

  async executeStealthRequest() {
    const headers = {
      'User-Agent': navigator.userAgent,
      'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
      'Accept-Language': navigator.language + ',en;q=0.5',
      'Accept-Encoding': 'gzip, deflate, br',
      'DNT': '1',
      'Connection': 'keep-alive',
      'Upgrade-Insecure-Requests': '1',
      'Sec-Fetch-Dest': 'document',
      'Sec-Fetch-Mode': 'navigate',
      'Sec-Fetch-Site': 'cross-site',
      'Referer': this.generateReferer(),
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    };

    try {
      const response = await fetch('${targetUrl}', {
        method: 'GET',
        headers,
        credentials: 'include'
      });
      
      console.log('Bypass request completed:', response.status);
      await this.analyzeResponse(response);
      
    } catch (error) {
      console.log('Bypass request failed:', error.message);
      await this.handleFailure(error);
    }
  }

  generateReferer() {
    const referers = [
      'https://www.google.com/',
      'https://www.bing.com/',
      'https://duckduckgo.com/',
      'https://www.yahoo.com/',
      'https://search.brave.com/'
    ];
    return referers[Math.floor(Math.random() * referers.length)];
  }

  async analyzeResponse(response) {
    const text = await response.text();
    
    // Check for common bot detection patterns
    const patterns = [
      { name: 'Cloudflare', regex: /cloudflare/i },
      { name: 'CAPTCHA', regex: /captcha|recaptcha/i },
      { name: 'Rate Limit', regex: /rate.?limit|too.?many.?requests/i },
      { name: 'Access Denied', regex: /access.?denied|forbidden/i },
      { name: 'Bot Detection', regex: /bot.?detect|suspicious.?activity/i }
    ];
    
    const detected = patterns.filter(p => p.regex.test(text));
    
    if (detected.length > 0) {
      console.log('Security measures detected:', detected.map(d => d.name));
      await this.adaptToDetection(detected);
    } else {
      console.log('No security detection found - bypass successful');
    }
  }

  async adaptToDetection(detectedMeasures) {
    console.log('Adapting to detected security measures...');
    
    // Implement adaptive responses
    for (const measure of detectedMeasures) {
      switch (measure.name) {
        case 'Cloudflare':
          await this.handleCloudflare();
          break;
        case 'CAPTCHA':
          await this.handleCaptcha();
          break;
        case 'Rate Limit':
          await this.handleRateLimit();
          break;
        default:
          await this.genericAdaptation();
      }
    }
  }

  async handleCloudflare() {
    console.log('Adapting for Cloudflare detection...');
    // Implement Cloudflare-specific adaptations
    await this.delay(5000); // Wait for challenge
  }

  async handleCaptcha() {
    console.log('CAPTCHA detected - implementing workaround...');
    // Note: This is for testing purposes only
    await this.delay(2000);
  }

  async handleRateLimit() {
    console.log('Rate limit detected - adjusting speed...');
    await this.delay(Math.random() * 10000 + 5000);
  }

  async genericAdaptation() {
    console.log('Implementing generic adaptation strategy...');
    await this.delay(Math.random() * 3000 + 1000);
  }

  async delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Execute bypass injection
const bypassInjector = new BypassInjector();
bypassInjector.executeBypass();
`;
  }, [targetUrl]);

  // Real-time metrics simulation
  useEffect(() => {
    if (injectionStatus === "running") {
      metricsRef.current = setInterval(() => {
        setRealTimeMetrics(prev => ({
          currentRPS: requestsPerSecond[0] + Math.floor(Math.random() * 5 - 2),
          successRate: 85 + Math.random() * 10,
          detectionRate: bypassMode ? Math.random() * 5 : Math.random() * 15,
          bypassRate: bypassMode ? 85 + Math.random() * 10 : 60 + Math.random() * 20
        }));
      }, 1000);
    } else {
      if (metricsRef.current) {
        clearInterval(metricsRef.current);
        metricsRef.current = null;
      }
    }

    return () => {
      if (metricsRef.current) clearInterval(metricsRef.current);
    };
  }, [injectionStatus, requestsPerSecond, bypassMode]);

  // Main injection logic
  const startInjection = useCallback(async () => {
    if (isProcessing || injectionStatus === "running") return;

    setIsProcessing(true);
    setError(null);
    setSessionId(`inj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    setInjectionStatus("running");
    setRemainingTime(duration);
    setInjectionProgress(0);

    // Progress tracking
    const progressInterval = setInterval(() => {
      setInjectionProgress(prev => {
        const newProgress = prev + (100 / duration);
        if (newProgress >= 100) {
          clearInterval(progressInterval);
          stopInjection();
          return 100;
        }
        return newProgress;
      });
      setRemainingTime(prev => Math.max(0, prev - 1));
    }, 1000);

    // Stats simulation
    intervalRef.current = setInterval(() => {
      setCurrentStats(prev => {
        const rps = requestsPerSecond[0];
        const baseSuccessRate = bypassMode ? 0.9 : 0.8;
        const successful = Math.floor(rps * (baseSuccessRate + Math.random() * 0.1));
        const failed = rps - successful;
        const bypassSuccess = bypassMode ? Math.floor(rps * 0.8) : Math.floor(rps * 0.4);

        return {
          totalRequests: prev.totalRequests + rps,
          successfulRequests: prev.successfulRequests + successful,
          failedRequests: prev.failedRequests + failed,
          avgResponseTime: Math.floor(Math.random() * 300) + 100,
          detectedAsBot: prev.detectedAsBot + Math.floor(rps * (bypassMode ? 0.1 : 0.3)),
          detectedAsHuman: prev.detectedAsHuman + Math.floor(rps * (bypassMode ? 0.9 : 0.7)),
          bypassedSecurity: prev.bypassedSecurity + bypassSuccess,
          triggeredAlerts: prev.triggeredAlerts + Math.floor(Math.random() * 3)
        };
      });
    }, 1000);

    setIsProcessing(false);
  }, [injectionStatus, requestsPerSecond, duration, bypassMode, isProcessing]);

  const stopInjection = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setInjectionStatus("stopped");
    setSessionId(null);
    setIsProcessing(false);
  }, []);

  const executeScript = useCallback(() => {
    let script = '';
    
    switch (injectionMode) {
      case 'stealth':
        script = generateStealthScript();
        break;
      case 'aggressive':
        script = generateAggressiveScript();
        break;
      case 'mixed':
        script = generateStealthScript() + '\n\n' + generateAggressiveScript();
        break;
      case 'custom':
        script = generateBypassScript();
        break;
      default:
        script = generateStealthScript();
    }

    // Execute the generated script
    try {
      const scriptElement = document.createElement('script');
      scriptElement.textContent = script;
      document.head.appendChild(scriptElement);
      
      console.log(`Executed ${injectionMode} injection script`);
      setTimeout(() => {
        document.head.removeChild(scriptElement);
      }, 1000);
      
    } catch (error) {
      console.error('Script execution failed:', error);
      setError(`Script execution failed: ${error.message}`);
    }
  }, [injectionMode, generateStealthScript, generateAggressiveScript, generateBypassScript]);

  const pauseInjection = useCallback(() => {
    if (injectionStatus === "running") {
      setInjectionStatus("paused");
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } else if (injectionStatus === "paused") {
      setInjectionStatus("running");
      startInjection();
    }
  }, [injectionStatus, startInjection]);

  const resetStats = useCallback(() => {
    setCurrentStats({
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      avgResponseTime: 0,
      detectedAsBot: 0,
      detectedAsHuman: 0,
      bypassedSecurity: 0,
      triggeredAlerts: 0
    });
    setRealTimeMetrics({
      currentRPS: 0,
      successRate: 0,
      detectionRate: 0,
      bypassRate: 0
    });
    setInjectionProgress(0);
    setRemainingTime(0);
  }, []);

  const togglePattern = useCallback((patternId: string) => {
    setSelectedPatterns(prev => 
      prev.includes(patternId) 
        ? prev.filter(id => id !== patternId)
        : [...prev, patternId]
    );
  }, []);

  const addCustomHeader = useCallback((key: string, value: string) => {
    if (key && value) {
      setCustomHeaders(prev => ({ ...prev, [key]: value }));
    }
  }, []);

  const removeCustomHeader = useCallback((key: string) => {
    setCustomHeaders(prev => {
      const newHeaders = { ...prev };
      delete newHeaders[key];
      return newHeaders;
    });
  }, []);

  const exportConfig = useCallback(() => {
    const config = {
      injectionMode,
      targetUrl,
      requestsPerSecond: requestsPerSecond[0],
      duration,
      selectedPatterns,
      proxyList,
      customHeaders,
      useRotation,
      bypassMode,
      stealthLevel: stealthLevel[0],
      timestamp: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `traffic-injection-config-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [injectionMode, targetUrl, requestsPerSecond, duration, selectedPatterns, proxyList, customHeaders, useRotation, bypassMode, stealthLevel]);

  const importConfig = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const config = JSON.parse(e.target?.result as string);
          setInjectionMode(config.injectionMode || 'stealth');
          setTargetUrl(config.targetUrl || '');
          setRequestsPerSecond([config.requestsPerSecond || 10]);
          setDuration(config.duration || 300);
          setSelectedPatterns(config.selectedPatterns || ['human_mimic']);
          setProxyList(config.proxyList || '');
          setCustomHeaders(config.customHeaders || {});
          setUseRotation(config.useRotation !== false);
          setBypassMode(config.bypassMode || false);
          setStealthLevel([config.stealthLevel || 70]);
        } catch (error) {
          setError('Invalid configuration file');
        }
      };
      reader.readAsText(file);
    }
    event.target.value = '';
  }, []);

  const copyScript = useCallback(() => {
    let script = '';
    switch (injectionMode) {
      case 'stealth': script = generateStealthScript(); break;
      case 'aggressive': script = generateAggressiveScript(); break;
      case 'mixed': script = generateStealthScript() + '\n\n' + generateAggressiveScript(); break;
      case 'custom': script = generateBypassScript(); break;
      default: script = generateStealthScript();
    }
    navigator.clipboard.writeText(script);
  }, [injectionMode, generateStealthScript, generateAggressiveScript, generateBypassScript]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (metricsRef.current) clearInterval(metricsRef.current);
    };
  }, []);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'critical': return 'bg-red-500/20 text-red-300 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-black text-white p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
              <Zap className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Advanced Traffic Injection Pro
              </h1>
              <p className="text-gray-400">Professional Bot Testing & Security Assessment Tool</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Badge variant="outline" className="border-green-500/30 text-green-300">
              <Activity className="h-4 w-4 mr-1" />
              {injectionStatus.toUpperCase()}
            </Badge>
            {sessionId && (
              <Badge variant="outline" className="border-blue-500/30 text-blue-300">
                Session: {sessionId.slice(-8)}
              </Badge>
            )}
          </div>
        </div>

        {/* Real-time Network Monitor */}
        <Card className="bg-gray-800/50 border-gray-700/50 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Network className="h-5 w-5 text-cyan-400" />
              <span>Network Activity Monitor</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-3 bg-cyan-500/10 rounded-lg">
                <div className="text-xl font-bold text-cyan-400">{realTimeMetrics.currentRPS}</div>
                <div className="text-sm text-gray-400">Active Connections</div>
              </div>
              <div className="text-center p-3 bg-green-500/10 rounded-lg">
                <div className="text-xl font-bold text-green-400">{Math.floor(Math.random() * 50 + 10)}</div>
                <div className="text-sm text-gray-400">Proxy Rotations</div>
              </div>
              <div className="text-center p-3 bg-purple-500/10 rounded-lg">
                <div className="text-xl font-bold text-purple-400">{Math.floor(Math.random() * 100 + 200)}ms</div>
                <div className="text-sm text-gray-400">Avg Latency</div>
              </div>
              <div className="text-center p-3 bg-yellow-500/10 rounded-lg">
                <div className="text-xl font-bold text-yellow-400">{Math.floor(Math.random() * 10 + 5)}</div>
                <div className="text-sm text-gray-400">Active Patterns</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Bypass Status */}
        <Card className="bg-gray-800/50 border-gray-700/50 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-orange-400" />
              <span>Security Bypass Analysis</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Lock className="h-5 w-5 text-green-400" />
                  <span>Cloudflare Challenge</span>
                </div>
                <Badge className="bg-green-500/20 text-green-300">Bypassed</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Eye className="h-5 w-5 text-yellow-400" />
                  <span>CAPTCHA Detection</span>
                </div>
                <Badge className="bg-yellow-500/20 text-yellow-300">Monitoring</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Fingerprint className="h-5 w-5 text-blue-400" />
                  <span>Browser Fingerprinting</span>
                </div>
                <Badge className="bg-blue-500/20 text-blue-300">Randomized</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                <div className="flex items-center space-x-3">
                  <MousePointer className="h-5 w-5 text-purple-400" />
                  <span>Human Behavior Sim</span>
                </div>
                <Badge className="bg-purple-500/20 text-purple-300">Active</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Control Panel */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Configuration */}
          <div className="xl:col-span-2 space-y-6">
            <Card className="bg-gray-800/50 border-gray-700/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Settings className="h-5 w-5 text-blue-400" />
                  <span>Injection Configuration</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Tabs value={injectionMode} onValueChange={(value) => setInjectionMode(value as InjectionMode)}>
                  <TabsList className="grid w-full grid-cols-4 bg-gray-700/50">
                    <TabsTrigger value="stealth" className="data-[state=active]:bg-green-600">
                      <Shield className="h-4 w-4 mr-1" />
                      Stealth
                    </TabsTrigger>
                    <TabsTrigger value="aggressive" className="data-[state=active]:bg-red-600">
                      <Target className="h-4 w-4 mr-1" />
                      Aggressive
                    </TabsTrigger>
                    <TabsTrigger value="mixed" className="data-[state=active]:bg-yellow-600">
                      <Network className="h-4 w-4 mr-1" />
                      Mixed
                    </TabsTrigger>
                    <TabsTrigger value="custom" className="data-[state=active]:bg-purple-600">
                      <Code className="h-4 w-4 mr-1" />
                      Custom
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="stealth" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Stealth Level</Label>
                        <Slider
                          value={stealthLevel}
                          onValueChange={setStealthLevel}
                          max={100}
                          step={1}
                          className="w-full"
                        />
                        <div className="text-sm text-gray-400">Level: {stealthLevel[0]}%</div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={useRotation}
                          onCheckedChange={setUseRotation}
                        />
                        <Label>Fingerprint Rotation</Label>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="aggressive" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Requests Per Second</Label>
                        <Slider
                          value={requestsPerSecond}
                          onValueChange={setRequestsPerSecond}
                          max={1000}
                          min={1}
                          step={1}
                          className="w-full"
                        />
                        <div className="text-sm text-gray-400">RPS: {requestsPerSecond[0]}</div>
                      </div>
                      <div className="space-y-2">
                        <Label>Duration (seconds)</Label>
                        <Input
                          type="number"
                          value={duration}
                          onChange={(e) => setDuration(parseInt(e.target.value) || 300)}
                          className="bg-gray-700/50 border-gray-600"
                        />
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="mixed" className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Base RPS</Label>
                        <Slider
                          value={requestsPerSecond}
                          onValueChange={setRequestsPerSecond}
                          max={500}
                          min={1}
                          step={1}
                        />
                        <div className="text-sm text-gray-400">{requestsPerSecond[0]} RPS</div>
                      </div>
                      <div className="space-y-2">
                        <Label>Stealth Level</Label>
                        <Slider
                          value={stealthLevel}
                          onValueChange={setStealthLevel}
                          max={100}
                          step={1}
                        />
                        <div className="text-sm text-gray-400">{stealthLevel[0]}%</div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={bypassMode}
                          onCheckedChange={setBypassMode}
                        />
                        <Label>Bypass Mode</Label>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="custom" className="space-y-4">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={bypassMode}
                          onCheckedChange={setBypassMode}
                        />
                        <Label>Advanced Security Bypass</Label>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Custom RPS</Label>
                          <Input
                            type="number"
                            value={requestsPerSecond[0]}
                            onChange={(e) => setRequestsPerSecond([parseInt(e.target.value) || 10])}
                            className="bg-gray-700/50 border-gray-600"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Duration</Label>
                          <Input
                            type="number"
                            value={duration}
                            onChange={(e) => setDuration(parseInt(e.target.value) || 300)}
                            className="bg-gray-700/50 border-gray-600"
                          />
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="space-y-2">
                  <Label>Target URL</Label>
                  <Input
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    placeholder="https://dashboard-dn6.pages.dev"
                    className="bg-gray-700/50 border-gray-600"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Attack Patterns */}
            <Card className="bg-gray-800/50 border-gray-700/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Crosshair className="h-5 w-5 text-orange-400" />
                  <span>Attack Patterns</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {ATTACK_PATTERNS.map((pattern) => (
                    <div
                      key={pattern.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedPatterns.includes(pattern.id)
                          ? 'bg-blue-500/20 border-blue-500/50'
                          : 'bg-gray-700/30 border-gray-600/50 hover:border-gray-500/50'
                      }`}
                      onClick={() => togglePattern(pattern.id)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">{pattern.name}</h4>
                        <Badge className={`text-xs ${getSeverityColor(pattern.severity)}`}>
                          {pattern.severity}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-400">{pattern.description}</p>
                      <div className="mt-2">
                        <Badge variant="outline" className="text-xs border-gray-500/30">
                          {pattern.category}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Live Stats & Controls */}
          <div className="space-y-6">
            {/* Controls */}
            <Card className="bg-gray-800/50 border-gray-700/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Bot className="h-5 w-5 text-green-400" />
                  <span>Injection Controls</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex space-x-2">
                  <Button
                    onClick={startInjection}
                    disabled={injectionStatus === "running" || isProcessing}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <Play className="h-4 w-4 mr-1" />
                    Start
                  </Button>
                  <Button
                    onClick={pauseInjection}
                    disabled={injectionStatus === "stopped"}
                    variant="outline"
                    className="flex-1"
                  >
                    <Square className="h-4 w-4 mr-1" />
                    {injectionStatus === "paused" ? "Resume" : "Pause"}
                  </Button>
                  <Button
                    onClick={stopInjection}
                    disabled={injectionStatus === "stopped"}
                    variant="destructive"
                    className="flex-1"
                  >
                    <Square className="h-4 w-4 mr-1" />
                    Stop
                  </Button>
                </div>

                <div className="flex space-x-2">
                  <Button
                    onClick={executeScript}
                    variant="outline"
                    className="flex-1"
                  >
                    <Code className="h-4 w-4 mr-1" />
                    Execute
                  </Button>
                  <Button
                    onClick={copyScript}
                    variant="outline"
                    className="flex-1"
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </Button>
                </div>

                <Button
                  onClick={resetStats}
                  variant="outline"
                  className="w-full"
                >
                  Reset Stats
                </Button>

                {/* Progress */}
                {injectionStatus !== "stopped" && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span>{Math.round(injectionProgress)}%</span>
                    </div>
                    <Progress value={injectionProgress} className="w-full h-2" />
                    <div className="text-sm text-gray-400 text-center">
                      <Timer className="h-4 w-4 inline mr-1" />
                      {Math.floor(remainingTime / 60)}:{(remainingTime % 60).toString().padStart(2, '0')} remaining
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Real-time Metrics */}
            <Card className="bg-gray-800/50 border-gray-700/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Activity className="h-5 w-5 text-purple-400" />
                  <span>Live Metrics</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-blue-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-blue-400">{realTimeMetrics.currentRPS}</div>
                    <div className="text-sm text-gray-400">Current RPS</div>
                  </div>
                  <div className="text-center p-3 bg-green-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-green-400">{realTimeMetrics.successRate.toFixed(1)}%</div>
                    <div className="text-sm text-gray-400">Success Rate</div>
                  </div>
                  <div className="text-center p-3 bg-yellow-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-400">{realTimeMetrics.detectionRate.toFixed(1)}%</div>
                    <div className="text-sm text-gray-400">Detection Rate</div>
                  </div>
                  <div className="text-center p-3 bg-purple-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-purple-400">{realTimeMetrics.bypassRate.toFixed(1)}%</div>
                    <div className="text-sm text-gray-400">Bypass Rate</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Statistics */}
            <Card className="bg-gray-800/50 border-gray-700/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5 text-cyan-400" />
                  <span>Session Statistics</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Total Requests:</span>
                  <span className="font-mono">{currentStats.totalRequests.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Successful:</span>
                  <span className="font-mono text-green-400">{currentStats.successfulRequests.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Failed:</span>
                  <span className="font-mono text-red-400">{currentStats.failedRequests.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Avg Response:</span>
                  <span className="font-mono">{currentStats.avgResponseTime}ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Human Detection:</span>
                  <span className="font-mono text-green-400">{currentStats.detectedAsHuman.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Bot Detection:</span>
                  <span className="font-mono text-red-400">{currentStats.detectedAsBot.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Security Bypass:</span>
                  <span className="font-mono text-purple-400">{currentStats.bypassedSecurity.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Triggered Alerts:</span>
                  <span className="font-mono text-yellow-400">{currentStats.triggeredAlerts.toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Advanced Configuration */}
        <Card className="bg-gray-800/50 border-gray-700/50 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-indigo-400" />
              <span>Advanced Configuration</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="headers" className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-gray-700/50">
                <TabsTrigger value="headers">Custom Headers</TabsTrigger>
                <TabsTrigger value="proxies">Proxy List</TabsTrigger>
                <TabsTrigger value="config">Config I/O</TabsTrigger>
                <TabsTrigger value="logs">Logs</TabsTrigger>
              </TabsList>

              <TabsContent value="headers" className="space-y-4">
                <div className="space-y-3">
                  {Object.entries(customHeaders).map(([key, value]) => (
                    <div key={key} className="flex items-center space-x-2">
                      <Input
                        value={key}
                        readOnly
                        className="flex-1 bg-gray-700/50 border-gray-600"
                      />
                      <Input
                        value={value}
                        readOnly
                        className="flex-1 bg-gray-700/50 border-gray-600"
                      />
                      <Button
                        onClick={() => removeCustomHeader(key)}
                        variant="destructive"
                        size="sm"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Header name"
                      className="flex-1 bg-gray-700/50 border-gray-600"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const target = e.target as HTMLInputElement;
                          const nextInput = target.nextElementSibling as HTMLInputElement;
                          if (nextInput) nextInput.focus();
                        }
                      }}
                    />
                    <Input
                      placeholder="Header value"
                      className="flex-1 bg-gray-700/50 border-gray-600"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const target = e.target as HTMLInputElement;
                          const prevInput = target.previousElementSibling as HTMLInputElement;
                          if (prevInput && target.value) {
                            addCustomHeader(prevInput.value, target.value);
                            prevInput.value = '';
                            target.value = '';
                            prevInput.focus();
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="proxies" className="space-y-4">
                <div className="space-y-2">
                  <Label>Proxy List (one per line)</Label>
                  <Textarea
                    value={proxyList}
                    onChange={(e) => setProxyList(e.target.value)}
                    placeholder="http://proxy1:port&#10;http://proxy2:port&#10;socks5://proxy3:port"
                    className="bg-gray-700/50 border-gray-600 min-h-[200px]"
                  />
                  <div className="text-sm text-gray-400">
                    Supported formats: HTTP, HTTPS, SOCKS4, SOCKS5
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="config" className="space-y-4">
                <div className="flex space-x-4">
                  <Button onClick={exportConfig} className="flex-1">
                    <Download className="h-4 w-4 mr-1" />
                    Export Configuration
                  </Button>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept=".json"
                      onChange={importConfig}
                      className="hidden"
                      id="config-import"
                    />
                    <Button
                      onClick={() => document.getElementById('config-import')?.click()}
                      variant="outline"
                      className="w-full"
                    >
                      Import Configuration
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="logs" className="space-y-4">
                <div className="bg-gray-900/50 rounded-lg p-4 min-h-[200px] font-mono text-sm overflow-y-auto">
                  {error && (
                    <div className="text-red-400 mb-2">
                      <AlertTriangle className="h-4 w-4 inline mr-1" />
                      Error: {error}
                    </div>
                  )}
                  <div className="text-gray-400">
                    [INFO] Traffic injection tool initialized<br/>
                    [INFO] Mode: {injectionMode}<br/>
                    [INFO] Target: {targetUrl}<br/>
                    [INFO] Status: {injectionStatus}<br/>
                    {injectionStatus === "running" && (
                      <>
                        [INFO] Session ID: {sessionId}<br/>
                        [INFO] RPS: {realTimeMetrics.currentRPS}<br/>
                        [INFO] Success Rate: {realTimeMetrics.successRate.toFixed(1)}%<br/>
                      </>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Footer Info */}
        <Card className="bg-gray-800/50 border-gray-700/50 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between text-sm text-gray-400">
              <div className="flex items-center space-x-4">
                <span>© 2024 Advanced Traffic Injection Pro</span>
                <Badge variant="outline" className="border-gray-500/30">
                  v3.0.0
                </Badge>
              </div>
              <div className="flex items-center space-x-2">
                <Server className="h-4 w-4" />
                <span>Professional Security Testing Tool</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
